/**
 * Canonical data for the twelve rashis. One source of truth used by the
 * home-page carousel and the per-rashi detail pages.
 *
 * Same content voice as grahas.js: attributions are stated AS attributions,
 * never as effects. No health, financial or outcome claims anywhere.
 * The stone of a rashi is the stone of its LORD — the classical scheme does
 * not give signs their own separate stones, and we say so on every page.
 */

export const RASHI_DETAILS = [
  {
    slug: "mesha",
    name: "Mesha",
    english: "Aries",
    deva: "मेष",
    symbol: "♈\uFE0E",
    symbol_label: "The ram",
    lord_slug: "mangal",
    lord_name: "Mangal — Mars",
    tattva: "Agni",
    tattva_en: "Fire",
    nature: "Chara",
    nature_en: "Movable",
    stone_slug: "red-coral",
    stone_name: "Red Coral",
    stone_hindi: "Moonga",
    accent: "#a03325",
    lede:
      "Mesha is the first rashi — the ram — ruled by Mangal in the classical scheme. It opens the zodiac, and the texts read it as the sign of beginnings, initiative and unhesitating action.",
    knowledge: [
      "Classical sources open the zodiac with Mesha. Its symbol is the ram, its tattva fire, and its nature chara — movable. The Vedic scheme reads a movable fire sign as the temperament of the starter: the one who moves first and settles later.",
      "Mesha is ruled by Mangal — Mars — and the texts read the rashi through its lord. Where Mangal is strong in a chart, the classical texts describe initiative and directness under Mesha placements; where he is afflicted, the same texts describe haste. Both are attributions, not outcome claims.",
      "The stone read for Mesha is the stone of its lord: red coral, moonga. The tradition does not give a rashi its own separate stone — it sends the wearer to the lord of the sign, and we say so rather than invent a thirteenth stone to sell.",
    ],
    why_it_is_worn:
      "Classical sources associate red coral with Mangal, the lord of Mesha. It is the stone traditionally read for those with prominent Mesha placements. The wording is deliberate: read for — not guaranteed to help.",
    pitch:
      "Red coral — moonga — is the stone of Mangal, the lord of Mesha. Each piece we hold is a one-of-one listing: origin stated, treatment stated, and the report number verifiable on the laboratory's own website before you pay.",
  },
  {
    slug: "vrishabha",
    name: "Vrishabha",
    english: "Taurus",
    deva: "वृषभ",
    symbol: "♉\uFE0E",
    symbol_label: "The bull",
    lord_slug: "shukra",
    lord_name: "Shukra — Venus",
    tattva: "Prithvi",
    tattva_en: "Earth",
    nature: "Sthira",
    nature_en: "Fixed",
    stone_slug: "white-sapphire",
    stone_name: "White Sapphire",
    stone_hindi: "Safed Pukhraj",
    accent: "#7d8499",
    lede:
      "Vrishabha is the second rashi — the bull — ruled by Shukra. A fixed earth sign, read in the classical texts as the temperament of steadiness, accumulation and the patient finishing of what Mesha starts.",
    knowledge: [
      "The symbol of Vrishabha is the sacred bull; its tattva is earth and its nature sthira — fixed. The classical scheme reads a fixed earth sign as the most settled temperament in the zodiac: slow to move, slower to leave.",
      "Vrishabha is ruled by Shukra — Venus — the graha of beauty and made things. The texts associate Vrishabha placements with an eye for quality and comfort where the lord is well-placed, and with holding on too long where he is afflicted. Attributions, not effects.",
      "The stone read for Vrishabha is the stone of its lord Shukra: diamond, or its accepted substitute white sapphire. The tradition itself allows the substitute — it is not spiritually inferior in the classical scheme, and we would rather sell you the substitute than a diamond you did not need.",
    ],
    why_it_is_worn:
      "Classical sources associate white sapphire — the accepted substitute for diamond — with Shukra, the lord of Vrishabha. It is the stone traditionally read for prominent Vrishabha placements.",
    pitch:
      "White sapphire is the accepted stone of Shukra, lord of Vrishabha — same graha as diamond, a fraction of the price. Every piece is certified and its report verifies on the laboratory's own website.",
  },
  {
    slug: "mithuna",
    name: "Mithuna",
    english: "Gemini",
    deva: "मिथुन",
    symbol: "♊\uFE0E",
    symbol_label: "The couple",
    lord_slug: "budh",
    lord_name: "Budh — Mercury",
    tattva: "Vayu",
    tattva_en: "Air",
    nature: "Dvisvabhava",
    nature_en: "Dual",
    stone_slug: "emerald",
    stone_name: "Emerald",
    stone_hindi: "Panna",
    accent: "#1f7a45",
    lede:
      "Mithuna is the third rashi — the couple — ruled by Budh. A dual air sign, read in the classical texts as the temperament of speech, exchange and quick learning.",
    knowledge: [
      "The symbol of Mithuna is a couple — classically a man with a mace and a woman with a veena — its tattva air, its nature dvisvabhava, dual. The scheme reads a dual air sign as the most conversational temperament of the twelve.",
      "Mithuna is ruled by Budh — Mercury — the graha of language and commerce. The texts associate Mithuna placements with fluency and negotiation where Budh is strong, and with scattered attention where he is afflicted. Both readings are quoted here as attributions.",
      "The stone read for Mithuna is the stone of its lord: emerald, panna. One rashi, one lord, one stone — that is how the classical scheme works, and it is why we do not invent extra 'lucky stones' per sign.",
    ],
    why_it_is_worn:
      "Classical sources associate emerald with Budh, the lord of Mithuna. It is the stone traditionally read for those with prominent Mithuna placements.",
    pitch:
      "Emerald — panna — is the stone of Budh, lord of Mithuna. Every emerald we hold is certified, one-of-one, and listed with the exact per-carat price so you can compare us against anyone.",
  },
  {
    slug: "karka",
    name: "Karka",
    english: "Cancer",
    deva: "कर्क",
    symbol: "♋\uFE0E",
    symbol_label: "The crab",
    lord_slug: "chandra",
    lord_name: "Chandra — Moon",
    tattva: "Jala",
    tattva_en: "Water",
    nature: "Chara",
    nature_en: "Movable",
    stone_slug: "pearl",
    stone_name: "Pearl",
    stone_hindi: "Moti",
    accent: "#6f7686",
    lede:
      "Karka is the fourth rashi — the crab — ruled by Chandra, the Moon. A movable water sign, read as the temperament of memory, home and the inner tide of mood.",
    knowledge: [
      "The symbol of Karka is the crab; its tattva water, its nature chara. It is the only rashi ruled by Chandra, and because the Moon moves faster than any other graha, the texts read Karka placements as the most responsive in the zodiac.",
      "Where Chandra is strong, the classical texts describe steadiness of feeling under Karka placements; where afflicted, the same texts describe unsettled tides. Neither is an outcome claim — they are what the sources say, quoted as attributions.",
      "The stone read for Karka is the stone of its lord: pearl, moti. It is traditionally the first stone a family buys, and the least expensive of the nine principal stones to begin with.",
    ],
    why_it_is_worn:
      "Classical sources associate pearl with Chandra, the lord of Karka. It is the stone traditionally read for prominent Karka placements and for unsettled periods of mind — worded as tradition, never as cure.",
    pitch:
      "Pearl — moti — is the stone of Chandra, lord of Karka. Ours are certified, honestly photographed, and each listing states exactly what it is: origin, surface, lustre, and a verifiable report.",
  },
  {
    slug: "simha",
    name: "Simha",
    english: "Leo",
    deva: "सिंह",
    symbol: "♌\uFE0E",
    symbol_label: "The lion",
    lord_slug: "surya",
    lord_name: "Surya — Sun",
    tattva: "Agni",
    tattva_en: "Fire",
    nature: "Sthira",
    nature_en: "Fixed",
    stone_slug: "ruby",
    stone_name: "Ruby",
    stone_hindi: "Manik",
    accent: "#8a2f22",
    lede:
      "Simha is the fifth rashi — the lion — ruled by Surya, the Sun. A fixed fire sign, read as the temperament of presence, command and the self as it is seen.",
    knowledge: [
      "The symbol of Simha is the lion; its tattva fire, its nature sthira. It is the only rashi ruled by Surya, and the classical texts read it as the seat of the Sun's own temperament — fixed, radiant, central.",
      "Where Surya is strong, the texts describe steadiness of purpose and natural authority under Simha placements. Where he is afflicted, the same texts describe pride without ground. Both statements are attributions from the sources, not promises from us.",
      "The stone read for Simha is the stone of its lord: ruby, manik. Ruby is the costliest of the nine on a per-carat basis, which is exactly why every one of ours carries a laboratory report you can verify before paying.",
    ],
    why_it_is_worn:
      "Classical sources associate ruby with Surya, the lord of Simha. It is the stone traditionally read for prominent Simha placements and at the start of leadership responsibilities.",
    pitch:
      "Ruby — manik — is the stone of Surya, lord of Simha. Every ruby we hold is one-of-one: origin stated, treatment stated, priced per carat in the open, report verifiable on the laboratory's own site.",
  },
  {
    slug: "kanya",
    name: "Kanya",
    english: "Virgo",
    deva: "कन्या",
    symbol: "♍\uFE0E",
    symbol_label: "The maiden",
    lord_slug: "budh",
    lord_name: "Budh — Mercury",
    tattva: "Prithvi",
    tattva_en: "Earth",
    nature: "Dvisvabhava",
    nature_en: "Dual",
    stone_slug: "emerald",
    stone_name: "Emerald",
    stone_hindi: "Panna",
    accent: "#1f7a45",
    lede:
      "Kanya is the sixth rashi — the maiden with the sheaf of grain — ruled by Budh. A dual earth sign, read as the temperament of discrimination, service and exact work.",
    knowledge: [
      "The symbol of Kanya is a maiden holding grain; its tattva earth, its nature dvisvabhava. The scheme reads a dual earth sign as the craftsman's temperament: exacting, useful, hard to satisfy.",
      "Kanya is the second rashi of Budh — Mercury rules both Mithuna and Kanya, the air side and the earth side of the same intelligence. In Kanya the texts read Budh as analysis; in Mithuna, as exchange.",
      "The stone read for Kanya is the stone of its lord: emerald, panna — the same stone as Mithuna, because the lord is the same. The tradition does not multiply stones per sign, and neither do we.",
    ],
    why_it_is_worn:
      "Classical sources associate emerald with Budh, the lord of Kanya. It is the stone traditionally read for prominent Kanya placements and for work in language, accounts and analysis.",
    pitch:
      "Emerald — panna — is the stone of Budh, lord of Kanya. Every emerald we hold is certified, one-of-one, and listed with the exact per-carat price in the open.",
  },
  {
    slug: "tula",
    name: "Tula",
    english: "Libra",
    deva: "तुला",
    symbol: "♎\uFE0E",
    symbol_label: "The balance",
    lord_slug: "shukra",
    lord_name: "Shukra — Venus",
    tattva: "Vayu",
    tattva_en: "Air",
    nature: "Chara",
    nature_en: "Movable",
    stone_slug: "white-sapphire",
    stone_name: "White Sapphire",
    stone_hindi: "Safed Pukhraj",
    accent: "#7d8499",
    lede:
      "Tula is the seventh rashi — the balance — ruled by Shukra. A movable air sign, read as the temperament of partnership, fairness and the weighing of sides.",
    knowledge: [
      "The symbol of Tula is the merchant's balance; its tattva air, its nature chara. It is the only rashi whose symbol is an object rather than a living being — the texts read that as the sign's detachment: it weighs, it does not take sides.",
      "Tula is the second rashi of Shukra, who rules both Vrishabha and Tula. In Vrishabha the texts read Shukra as possession and comfort; in Tula, as agreement and proportion. Where the lord is afflicted, the same texts describe indecision. Attributions, not effects.",
      "The stone read for Tula is the stone of its lord: diamond, or its accepted substitute white sapphire — the tradition allows the substitute without loss, and we stock the substitute deliberately.",
    ],
    why_it_is_worn:
      "Classical sources associate white sapphire — the accepted diamond substitute — with Shukra, the lord of Tula. It is the stone traditionally read for prominent Tula placements.",
    pitch:
      "White sapphire is the stone of Shukra, lord of Tula — the tradition's own accepted substitute for diamond. Certified, one-of-one, verifiable on the laboratory's website before you pay.",
  },
  {
    slug: "vrishchika",
    name: "Vrishchika",
    english: "Scorpio",
    deva: "वृश्चिक",
    symbol: "♏\uFE0E",
    symbol_label: "The scorpion",
    lord_slug: "mangal",
    lord_name: "Mangal — Mars",
    tattva: "Jala",
    tattva_en: "Water",
    nature: "Sthira",
    nature_en: "Fixed",
    stone_slug: "red-coral",
    stone_name: "Red Coral",
    stone_hindi: "Moonga",
    accent: "#a03325",
    lede:
      "Vrishchika is the eighth rashi — the scorpion — ruled by Mangal. A fixed water sign, read as the temperament of intensity, depth and what is held beneath the surface.",
    knowledge: [
      "The symbol of Vrishchika is the scorpion; its tattva water, its nature sthira. The classical texts treat it as the most guarded of the twelve — a fixed water sign holds, and does not easily show what it holds.",
      "Vrishchika is the second rashi of Mangal, who also rules Mesha. In Mesha the texts read Mars as open assertion; in Vrishchika, as controlled force. Where the lord is afflicted, the same texts describe suspicion. Both are attributions from the sources.",
      "The stone read for Vrishchika is the stone of its lord: red coral, moonga — the same stone as Mesha, because the lord is the same. One lord, one stone; the scheme is consistent and we keep to it.",
    ],
    why_it_is_worn:
      "Classical sources associate red coral with Mangal, the lord of Vrishchika. It is the stone traditionally read for prominent Vrishchika placements.",
    pitch:
      "Red coral — moonga — is the stone of Mangal, lord of Vrishchika. Each piece is a one-of-one listing with origin, treatment and a report that verifies on the laboratory's own website.",
  },
  {
    slug: "dhanu",
    name: "Dhanu",
    english: "Sagittarius",
    deva: "धनु",
    symbol: "♐\uFE0E",
    symbol_label: "The archer",
    lord_slug: "guru",
    lord_name: "Guru — Jupiter",
    tattva: "Agni",
    tattva_en: "Fire",
    nature: "Dvisvabhava",
    nature_en: "Dual",
    stone_slug: "yellow-sapphire",
    stone_name: "Yellow Sapphire",
    stone_hindi: "Pukhraj",
    accent: "#a8821c",
    lede:
      "Dhanu is the ninth rashi — the archer — ruled by Guru, Jupiter. A dual fire sign, read as the temperament of aim, teaching and the long view.",
    knowledge: [
      "The symbol of Dhanu is the archer — in the Indian tradition a figure half man, half horse, bow drawn; its tattva fire, its nature dvisvabhava. The texts read it as directed fire: aimed, not scattered.",
      "Dhanu is ruled by Guru, the teacher of the devas. Where Guru is strong, the classical texts describe conviction and counsel under Dhanu placements; where afflicted, over-reach. Quoted as attributions, never as promises.",
      "The stone read for Dhanu is the stone of its lord: yellow sapphire, pukhraj — the most-asked-for stone in India, and the most mis-sold, which is why every one of ours carries a verifiable laboratory report.",
    ],
    why_it_is_worn:
      "Classical sources associate yellow sapphire with Guru, the lord of Dhanu. It is the stone traditionally read for prominent Dhanu placements, at marriages and at the start of study.",
    pitch:
      "Yellow sapphire — pukhraj — is the stone of Guru, lord of Dhanu. Ours arrive with a lab report you can check on the laboratory's own site before paying a rupee.",
  },
  {
    slug: "makara",
    name: "Makara",
    english: "Capricorn",
    deva: "मकर",
    symbol: "♑\uFE0E",
    symbol_label: "The makara",
    lord_slug: "shani",
    lord_name: "Shani — Saturn",
    tattva: "Prithvi",
    tattva_en: "Earth",
    nature: "Chara",
    nature_en: "Movable",
    stone_slug: "blue-sapphire",
    stone_name: "Blue Sapphire",
    stone_hindi: "Neelam",
    accent: "#2e4a86",
    lede:
      "Makara is the tenth rashi — the mythical sea-creature — ruled by Shani, Saturn. A movable earth sign, read as the temperament of climb, structure and work rewarded slowly.",
    knowledge: [
      "The symbol of Makara is the makara itself — part crocodile, part fish, a creature that moves through both water and earth; its tattva earth, its nature chara. The texts read it as the sign of the long climb.",
      "Makara is ruled by Shani, the slowest of the visible grahas and the one most cautioned-about in the classical texts. Where Shani is strong, long labour is rewarded under Makara placements; where afflicted, delay without conclusion. Both are what the sources say.",
      "The stone read for Makara is the stone of its lord: blue sapphire, neelam — the most cautioned-about stone in the tradition, which is why the classical scheme itself recommends a trial period, and why we honour one.",
    ],
    why_it_is_worn:
      "Classical sources associate blue sapphire with Shani, the lord of Makara. It is the stone traditionally read for prominent Makara placements — always with the trial period the tradition itself recommends.",
    pitch:
      "Blue sapphire — neelam — is the stone of Shani, lord of Makara. Every one of ours comes with the classical trial period honoured and a report that verifies on the laboratory's own website.",
  },
  {
    slug: "kumbha",
    name: "Kumbha",
    english: "Aquarius",
    deva: "कुम्भ",
    symbol: "♒\uFE0E",
    symbol_label: "The water-bearer",
    lord_slug: "shani",
    lord_name: "Shani — Saturn",
    tattva: "Vayu",
    tattva_en: "Air",
    nature: "Sthira",
    nature_en: "Fixed",
    stone_slug: "blue-sapphire",
    stone_name: "Blue Sapphire",
    stone_hindi: "Neelam",
    accent: "#2e4a86",
    lede:
      "Kumbha is the eleventh rashi — the water-bearer — ruled by Shani. A fixed air sign, read as the temperament of systems, assemblies and ideas held for the long term.",
    knowledge: [
      "The symbol of Kumbha is a man carrying the water pot; its tattva air, its nature sthira. The texts read a fixed air sign as conviction: ideas held, defended and slowly distributed — the pot is carried before it is poured.",
      "Kumbha is the second rashi of Shani, who also rules Makara — and in parts of the tradition it is associated with Rahu as co-lord. Where sources differ on how much weight to give Rahu here, we say so rather than pick silently.",
      "The stone read for Kumbha is the stone of its lord: blue sapphire, neelam — the same stone as Makara. The tradition's trial-period caution applies here exactly as it does there.",
    ],
    why_it_is_worn:
      "Classical sources associate blue sapphire with Shani, the lord of Kumbha. It is the stone traditionally read for prominent Kumbha placements — with the same trial period the tradition recommends.",
    pitch:
      "Blue sapphire — neelam — is the stone of Shani, lord of Kumbha. Certified, one-of-one, trial period honoured, and the report verifies on the laboratory's own site.",
  },
  {
    slug: "meena",
    name: "Meena",
    english: "Pisces",
    deva: "मीन",
    symbol: "♓\uFE0E",
    symbol_label: "The two fish",
    lord_slug: "guru",
    lord_name: "Guru — Jupiter",
    tattva: "Jala",
    tattva_en: "Water",
    nature: "Dvisvabhava",
    nature_en: "Dual",
    stone_slug: "yellow-sapphire",
    stone_name: "Yellow Sapphire",
    stone_hindi: "Pukhraj",
    accent: "#a8821c",
    lede:
      "Meena is the twelfth rashi — the two fish — ruled by Guru. A dual water sign, the last of the zodiac, read as the temperament of dissolution, compassion and the closing of the circle.",
    knowledge: [
      "The symbol of Meena is two fish swimming in opposite directions; its tattva water, its nature dvisvabhava. As the final rashi the texts read it as the zodiac completing itself — what began as Mesha's first movement ends as Meena's letting go.",
      "Meena is the second rashi of Guru, who also rules Dhanu. In Dhanu the texts read Jupiter as aim; in Meena, as faith. Where the lord is afflicted, the same texts describe drift. Attributions from the sources, quoted as such.",
      "The stone read for Meena is the stone of its lord: yellow sapphire, pukhraj — the same stone as Dhanu, because the lord is the same. One lord, one stone, no invented extras.",
    ],
    why_it_is_worn:
      "Classical sources associate yellow sapphire with Guru, the lord of Meena. It is the stone traditionally read for prominent Meena placements.",
    pitch:
      "Yellow sapphire — pukhraj — is the stone of Guru, lord of Meena. Every piece is certified and its report checks out on the laboratory's own website before you pay.",
  },
];

export function rashiBySlug(slug) {
  return RASHI_DETAILS.find((r) => r.slug === slug);
}
